import React, { useState, useEffect, useRef, useMemo } from 'react';
import { isAISummariesAccessible, useProfileParams } from '../../../../../../utils/common';
import KPITile from '../../../../../common/KPITile/KPITile';
import KPIDetailTile from '../../../../../common/KPIDetailTile/KPIDetailTile';
import DonutChart, { DonutChartSegment } from '../../../../../common/DonutChart/DonutChart';
import InsightAI from '../../../../../common/InsightAI/InsightAI';
import { useTranslation } from 'react-i18next';
import ViewModeSwitcher, {
  ViewMode,
} from '../../../../../common/ViewModeSwitcher/ViewModeSwitcher';
import Skeleton from '../../../../../common/Skeleton/Skeleton';
import { useGetBusinessPerformanceDetailsQuery } from '../../../../../../redux/services/businessPerformaceAPI';
import { useGetAGCAIDetailsMutation } from '../../../../../../redux/services/agcAIAPI';
import styles from './SubCategory.module.scss';
import KPITableWithPills from '../../../common/KPITableWithPills/KPITableWithPills';
import KPIAccordionTable from '../../../common/KPIAccordionTable/KPIAccordionTable';
import {
  sanitizeTableData,
  filterZeroRows,
} from '../../../../../../utils/expandableTableDataSanitizer';
import { getCurrentPerformanceYear } from '../../../../../../utils/dateFormatter';
import { getYearRange } from '../../../../../../utils/yearRangeExtractor';

interface SummaryProp {
  summary?: any;
  loading?: boolean;
}

export const SubCategory: React.FC<SummaryProp> = ({ summary, loading = false }) => {
  // Display API-provided segments when available; otherwise show nothing (0 fallback is implicit per segment values)
  const segments: DonutChartSegment[] = Array.isArray(summary?.segments) ? summary!.segments : [];
  const { t } = useTranslation();

  return (
    <KPITile
      title={t('sub_category')}
      dataTestId="sub-category-tile"
      loading={loading}
      className={styles.center_content}
    >
      <div className={styles.tile_content}>
        <DonutChart
          size={125}
          strokeWidth={20}
          legendGap={24}
          segments={segments}
          showLegend
          isPortfolio
          truncateDecimals
        />
      </div>
    </KPITile>
  );
};

export const SubCategoryDetails: React.FC<{
  kpiCode?: string;
  onClose?: () => void;
}> = ({ kpiCode = 'sub_category', onClose }) => {
  type View = 'perfYear' | 'monthly' | 'yearly';
  const { affiliateCode, aboNumber } = useProfileParams();
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>('left');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('perfYear');

  const { data: detailsResponse, isLoading } = useGetBusinessPerformanceDetailsQuery(
    { affiliateCode, aboNumber, payload: { kpis: [{ code: kpiCode }] } },
    { skip: !onClose }
  );

  const [getAGCAIDetails, { data: aiDetailsResp, isLoading: isLoadingAiSummary }] =
    useGetAGCAIDetailsMutation();
  const hasRequestedRef = useRef(false);

  useEffect(() => {
    const canFetch = Number(affiliateCode) !== 0 && aboNumber !== 0 && Boolean(onClose);
    if (!canFetch || hasRequestedRef.current) return;
    hasRequestedRef.current = true;
    getAGCAIDetails({
      affiliateCode,
      aboNumber,
      payload: { kpis: [{ code: kpiCode }] },
    });
  }, [affiliateCode, aboNumber, onClose, kpiCode, getAGCAIDetails]);

  const kpiData = useMemo(() => {
    if (detailsResponse?.body?.kpis) {
      return detailsResponse.body.kpis.find((k: any) => k.kpiCode === kpiCode);
    }
    return null;
  }, [detailsResponse, kpiCode]);

  const ytdViewData = kpiData?.detail?.ytdView?.chart.find(
    (c: { chartType: string; chartDetail: any }) => c.chartType === 'table'
  )?.chartDetail?.tableDetail;

  const monthlyRawTableData = kpiData?.detail?.monthlyView?.chart.find(
    (c: { chartType: string; chartDetail: any }) => c.chartType === 'table'
  )?.chartDetail?.tableDetail;

  const monthlyTableData = useMemo(
    () => filterZeroRows(sanitizeTableData(monthlyRawTableData)),
    [monthlyRawTableData]
  );

  const yearlyRawTableData = kpiData?.detail?.annuallyView?.chart.find(
    (c: { chartType: string; chartDetail: any }) => c.chartType === 'table'
  )?.chartDetail?.tableDetail;

  const yearlyTableData = useMemo(
    () => filterZeroRows(sanitizeTableData(yearlyRawTableData)),
    [yearlyRawTableData]
  );

  const pyYear =
    getCurrentPerformanceYear(detailsResponse?.body?.closingMonth)?.toString()?.slice(2) ?? '';

  const viewMap: Record<View, JSX.Element> = {
    perfYear: (
      <KPITableWithPills
        data={ytdViewData}
        affiliateCode={affiliateCode}
        applyGrouping={true}
        title="subcategory.subtitle"
        subtitle="topproducts.monthly_table_description"
        performanceYear={pyYear}
        truncateDecimals
      />
    ),
    monthly: (
      <KPIAccordionTable
        data={monthlyTableData}
        affiliateCode={affiliateCode}
        kpiCode={kpiCode}
        showPills={true}
        title={t('subcategory.monthly_title')}
        subtitle={t('categorymix.sales_performance_description')}
        groupedRowsSize={2}
        truncateDecimals
      />
    ),
    yearly: (
      <KPIAccordionTable
        data={yearlyTableData}
        affiliateCode={affiliateCode}
        kpiCode={kpiCode}
        showPills={true}
        groupedColumnClass
        highlightColumns={[1, 3]}
        title={t('subcategory.yearly_title', {
          startYear: getYearRange(null, monthlyRawTableData)?.startYear,
          endYear: getYearRange(null, monthlyRawTableData)?.endYear,
        })}
        subtitle={t('categorymix.sales_performance_description')}
        truncateDecimals
      />
    ),
  };

  const aiRaw = aiDetailsResp?.body;
  const aiResolved = typeof aiRaw === 'string' ? aiRaw : aiRaw?.[kpiCode] || aiRaw?.summary || '';
  const aiContent = aiResolved ? String(aiResolved).slice(0, 300).concat('...') : '';

  const performanceYear = `PY ${pyYear}`;
  const periodItems = useMemo(
    () => [
      { code: 'perfYear', label: performanceYear },
      { code: 'monthly', label: t('monthly') },
      { code: 'yearly', label: t('yearly') },
    ],
    [performanceYear, t]
  );

  const headerContent = (
    <div className={styles.header_content}>
      <div className={styles.tile_header}>
        <div className={styles.title}>{t('subcategory.title')}</div>
        <div className={styles.desc}>{t('subcategory.description')}</div>
      </div>
      <div className={styles.chart_view_wrapper}>
        <ViewModeSwitcher
          mode={viewMode}
          onChangeMode={setViewMode}
          disabled={false}
          showSwitch={false}
          selectedTimeRangeCode={selectedPeriod}
          onSelectTimeRange={setSelectedPeriod}
          timeRanges={periodItems}
        />
      </div>
    </div>
  );

  const leftContent = viewMap[selectedPeriod as View] ?? null;

  const rightContent = isAISummariesAccessible() ? (
    isLoadingAiSummary ? (
      <Skeleton height="200px" />
    ) : (
      <InsightAI
        updateDate=""
        content={aiContent}
        title={t('ai_summary')}
        showDropdown={false}
        isDropdownEnabled={true}
        flowerIconSize={24}
        titleSize={14}
      />
    )
  ) : null;

  if (isLoading) return <Skeleton height="400px" />;

  return (
    <KPIDetailTile
      headerContent={headerContent}
      leftContent={leftContent}
      rightContent={rightContent}
      onClose={onClose!}
      dataTestId="sub_category_detail"
    />
  );
};

export default SubCategory;
